import { type TFunction } from 'i18next';
import { SubscriptionType } from '@/types/shared';
import { I18N_KEYS } from '@/language/keys';

const keys = I18N_KEYS.PAYMENTS.PRICING_TABLE;

interface PricingConfig {
  name: string;
  planType: string;
  description: string;
  monthlyPrice: number;
  /** Rounded monthly equivalent shown for annual billing. */
  annualPrice: number;
  monthlyCredits: number;
  monthlyCreditsPerUser?: boolean;
  creditsInfoKey: string;
  creditsInfo: {
    numImages: number;
    numSeconds?: number;
    numMinutes?: number;
  };
}

/** Feature copy translated by the pricing table. */
interface PricingFeatureCopy {
  key: string;
  values?: {
    price: number;
    credits: number;
  };
  isUnavailable?: boolean;
}

/** Shared plan presentation metadata before translation. */
interface PricingTableConfig extends PricingConfig {
  features: readonly PricingFeatureCopy[];
  highlightedFeatureIndex?: number;
  isPopular?: boolean;
}

export const ANNUAL_DISCOUNT_PERCENT_20 = 20;
export const ANNUAL_DISCOUNT_PERCENT_30 = 30;

/** Column order for plan comparisons, including the individual Max plan after Team. */
export const PRICING_COMPARISON_PLAN_ORDER = [
  SubscriptionType.Free,
  SubscriptionType.Starter,
  SubscriptionType.Pro,
  SubscriptionType.Team,
  SubscriptionType.Max,
] as const;

export const INDIVIDUAL_PLANS: readonly SubscriptionType[] = [
  SubscriptionType.Free,
  SubscriptionType.Starter,
  SubscriptionType.Pro,
  SubscriptionType.Max,
];

/** Max launch offer displayed alongside annual pricing. */
export const MAX_LAUNCH_PROMOTION = {
  discountPercent: 25,
  model: 'Seedance 2.5',
  signupDeadline: new Date('2026-11-15T23:59:59.999Z'),
} as const;

export const getAnnualDiscountPercent = (isAnnualDiscount30Enabled: boolean): number =>
  isAnnualDiscount30Enabled ? ANNUAL_DISCOUNT_PERCENT_30 : ANNUAL_DISCOUNT_PERCENT_20;

export const PREVIOUS_PRICING_PLAN: Partial<Record<SubscriptionType, SubscriptionType>> = {
  [SubscriptionType.Starter]: SubscriptionType.Free,
  [SubscriptionType.Pro]: SubscriptionType.Starter,
  [SubscriptionType.Max]: SubscriptionType.Pro,
  // Team shares Pro's rollover benefits; Max's individual-plan benefits are not included.
  [SubscriptionType.Team]: SubscriptionType.Pro,
};

/** Plan metadata and translation keys shared by the pricing table and paywalls. */
export const getPlanMetadata = (isMaxTierLayout = false) =>
  ({
    [SubscriptionType.Free]: {
      name: keys.FREE_PLAN.NAME,
      planType: SubscriptionType.Free,
      description: keys.FREE_PLAN.DESCRIPTION,
      monthlyPrice: 0,
      annualPrice: 0,
      monthlyCredits: 150,
      creditsInfoKey: keys.CREDITS_INFO_IMAGES,
      creditsInfo: {
        numImages: 375,
      },
      features: [
        { key: keys.FEATURE.NO_VIDEO_MODELS, isUnavailable: true },
        { key: keys.FEATURE.NO_WORKFLOW_HISTORY, isUnavailable: true },
        { key: keys.FEATURE.PROFESSIONAL_GRADE_EDITING_TOOLS },
        { key: keys.FEATURE.FIVE_WORKFLOWS },
      ],
    },
    [SubscriptionType.Starter]: {
      name: keys.STARTER_PLAN.NAME,
      planType: SubscriptionType.Starter,
      description: keys.STARTER_PLAN.DESCRIPTION,
      monthlyPrice: 24,
      annualPrice: 19,
      monthlyCredits: 1500,
      creditsInfoKey: keys.CREDITS_INFO_VIDEO_MINUTES,
      creditsInfo: {
        numImages: 3750,
        numSeconds: 417,
        numMinutes: 7,
      },
      features: [
        { key: keys.FEATURE.ALL_MODELS },
        { key: keys.FEATURE.UNLIMITED_WORKFLOWS },
        { key: keys.FEATURE.CREDIT_TOP_UP, values: { price: 10, credits: 1000 } },
      ],
    },
    [SubscriptionType.Pro]: {
      name: keys.PRO_PLAN.NAME,
      planType: SubscriptionType.Pro,
      description: keys.PRO_PLAN.DESCRIPTION,
      monthlyPrice: 45,
      annualPrice: 36,
      monthlyCredits: 4000,
      creditsInfoKey: keys.CREDITS_INFO_VIDEO_MINUTES,
      creditsInfo: {
        numImages: 10000,
        numSeconds: 1111,
        numMinutes: 18,
      },
      features: [
        { key: keys.FEATURE.THREE_MONTH_CREDIT_ROLLOVER },
        { key: keys.FEATURE.CREDIT_TOP_UP, values: { price: 10, credits: 1200 } },
      ],
      highlightedFeatureIndex: 1,
      isPopular: true,
    },
    [SubscriptionType.Max]: {
      name: keys.MAX_PLAN.NAME,
      planType: SubscriptionType.Max,
      description: keys.MAX_PLAN.DESCRIPTION,
      monthlyPrice: 100,
      annualPrice: 70,
      monthlyCredits: 10000,
      creditsInfoKey: keys.CREDITS_INFO_VIDEO_MINUTES,
      creditsInfo: { numImages: 25000, numMinutes: 46 },
      features: [{ key: keys.FEATURE.FOUR_MONTH_CREDIT_ROLLOVER }, { key: keys.FEATURE.LOWEST_COST_PER_CREDIT }],
    },
    [SubscriptionType.Team]: {
      name: keys.TEAM_PLAN.NAME,
      planType: SubscriptionType.Team,
      description: isMaxTierLayout ? keys.TEAM_DESCRIPTION_MAX : keys.TEAM_PLAN.DESCRIPTION,
      monthlyPrice: 60,
      annualPrice: 48,
      monthlyCredits: 4500,
      monthlyCreditsPerUser: true,
      creditsInfoKey: keys.CREDITS_INFO_VIDEO_MINUTES,
      creditsInfo: {
        numImages: 11250,
        numSeconds: 1250,
        numMinutes: 20,
      },
      features: isMaxTierLayout
        ? [
            { key: keys.FEATURE.UNIFIED_BILLING },
            { key: keys.FEATURE.SHARED_CREDIT_POOL },
            { key: keys.FEATURE.WORKSPACE_FILE_SHARING },
            { key: keys.FEATURE.TEAM_CREDITS_MANAGEMENT },
            { key: keys.FEATURE.TEAM_MEMBERS_ADMINISTRATION },
          ]
        : [
            { key: keys.FEATURE.WORKSPACE_FILE_SHARING },
            { key: keys.FEATURE.SHARED_CREDIT_POOL },
            { key: keys.FEATURE.UNIFIED_BILLING },
            { key: keys.FEATURE.ADVANCED_ADMIN },
          ],
    },
  }) as const;

const FIRST_MONTH_DISCOUNT_RATE: Partial<Record<PricingPlanType, number>> = {
  [SubscriptionType.Starter]: 0.2,
  [SubscriptionType.Pro]: 0.2,
  [SubscriptionType.Team]: 0.5,
};

export type PricingPlanType = keyof ReturnType<typeof getPlanMetadata>;

/** Calculates the displayed monthly equivalent and yearly charge from an undiscounted monthly price. */
export const calculateAnnualPrices = (monthlyPrice: number, discountPercent: number) => {
  const billedPercent = 100 - discountPercent;
  return {
    annualPrice: Math.round((monthlyPrice * billedPercent) / 100),
    annualTotal: Math.round((monthlyPrice * 12 * billedPercent) / 100),
  };
};

export const formatPrice = (amount: number): string => {
  const cents = Math.round(amount * 100);
  // Weave pricing currently supports USD in English only.
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
};

/** Select display and billed prices together; flag-off preserves the legacy prices exactly. */
export const getPlanPrices = (plan: PricingPlanType, isAnnualDiscount30Enabled: boolean) => {
  const { monthlyPrice, annualPrice } = getPlanMetadata()[plan];
  // Max launches with its 30%-off annual price; the annual-discount experiment changes other tiers.
  const selectedAnnualPrices =
    isAnnualDiscount30Enabled && plan !== SubscriptionType.Max
      ? calculateAnnualPrices(monthlyPrice, ANNUAL_DISCOUNT_PERCENT_30)
      : { annualPrice, annualTotal: annualPrice * 12 };
  const firstMonthDiscountRate = FIRST_MONTH_DISCOUNT_RATE[plan];
  return {
    monthlyPrice,
    ...selectedAnnualPrices,
    firstInvoicePrices:
      firstMonthDiscountRate === undefined
        ? undefined
        : {
            monthly: Math.round(monthlyPrice * (1 - firstMonthDiscountRate)),
            yearly: Math.round(
              selectedAnnualPrices.annualTotal - selectedAnnualPrices.annualPrice * firstMonthDiscountRate,
            ),
          },
  };
};

export const getFeaturesTitle = (subscriptionType: string, t: TFunction) => {
  // Free planCard don't need feature title "Everything in {{planType}}, plus -"
  if (subscriptionType === SubscriptionType.Free || !PREVIOUS_PRICING_PLAN[subscriptionType]) return undefined;

  const previousPlanType = PREVIOUS_PRICING_PLAN[subscriptionType]!;
  return t(keys.FEATURES_TITLE, {
    planType: t(getPlanMetadata()[previousPlanType].name),
  });
};

/** Builds one translated plan from the shared pricing configuration. */
export const getPricingPlanDetails = (
  t: TFunction,
  planType: PricingPlanType,
  { isAnnualDiscount30Enabled = false, isMaxTierLayout = false } = {},
) => {
  const metadata = getPlanMetadata(isMaxTierLayout);
  const {
    name,
    description,
    monthlyCredits,
    monthlyCreditsPerUser,
    creditsInfoKey,
    creditsInfo,
    features,
    highlightedFeatureIndex,
    isPopular,
  }: PricingTableConfig = metadata[planType];

  return {
    planType,
    name: t(name),
    description: t(description),
    credits: t(monthlyCreditsPerUser === true ? keys.MONTHLY_CREDITS_PER_USER : keys.MONTHLY_CREDITS, {
      monthlyCredits,
    }),
    creditsInfo: t(creditsInfoKey, { ...creditsInfo }),
    featuresTitle: getFeaturesTitle(planType, t),
    features: features.map(({ key, values, isUnavailable }) => ({ text: t(key, values), isUnavailable })),
    highlightedFeatureIndex,
    isPopular,
    ...getPlanPrices(planType, isAnnualDiscount30Enabled),
  };
};

/** Builds the existing translated packages for pricing and paywalls; Max is opted into separately. */
export const getPricingDetails = (
  t: TFunction,
  { isAnnualDiscount30Enabled = false, isMaxTierLayout = false } = {},
) => {
  const options = { isAnnualDiscount30Enabled, isMaxTierLayout };
  return {
    [SubscriptionType.Free]: getPricingPlanDetails(t, SubscriptionType.Free, options),
    [SubscriptionType.Starter]: getPricingPlanDetails(t, SubscriptionType.Starter, options),
    [SubscriptionType.Pro]: getPricingPlanDetails(t, SubscriptionType.Pro, options),
    [SubscriptionType.Team]: getPricingPlanDetails(t, SubscriptionType.Team, options),
  };
};

/** A translated plan package consumed directly by both pricing surfaces. */
export type PricingDetails = ReturnType<typeof getPricingDetails>[keyof ReturnType<typeof getPricingDetails>];
